import React, { useState } from 'react';
import { Ticket, Mountain, Landmark, PartyPopper, Compass, Star, Send, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITIES = [
  { name: 'Theme Parks', icon: <PartyPopper className="w-8 h-8" />, desc: 'Disneyland, Universal Studios, Ferrari World, and more', items: [
    { title: 'Dubai Parks & Resorts', location: 'Dubai', price: 'Rs.3,500', image: 'https://images.unsplash.com/photo-1563911302283-d2bc129e7570?w=400' },
    { title: 'Universal Studios Singapore', location: 'Singapore', price: 'Rs.4,200', image: 'https://images.unsplash.com/photo-1536768139911-e290a59011e4?w=400' },
    { title: 'Disneyland Hong Kong', location: 'Hong Kong', price: 'Rs.5,500', image: 'https://images.unsplash.com/photo-1597466599360-3b9775841aec?w=400' },
  ]},
  { name: 'City Tours', icon: <Landmark className="w-8 h-8" />, desc: 'Guided tours of the world\'s most iconic cities', items: [
    { title: 'Dubai City Tour', location: 'Dubai', price: 'Rs.2,000', image: 'https://images.unsplash.com/flagged/photo-1559717865-a99cac1c95d8?w=400' },
    { title: 'Paris Walking Tour', location: 'France', price: 'Rs.3,000', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400' },
    { title: 'Bangkok Temple Tour', location: 'Thailand', price: 'Rs.1,500', image: 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=400' },
  ]},
  { name: 'Adventure', icon: <Mountain className="w-8 h-8" />, desc: 'Skydiving, bungee jumping, scuba diving, and more', items: [
    { title: 'Skydiving Dubai', location: 'Dubai', price: 'Rs.18,000', image: 'https://images.unsplash.com/photo-1521673461164-de300ebcfb17?w=400' },
    { title: 'Bali Scuba Diving', location: 'Bali', price: 'Rs.5,000', image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400' },
    { title: 'Goa Water Sports', location: 'Goa', price: 'Rs.2,500', image: 'https://images.unsplash.com/photo-1530053969600-caed2596d242?w=400' },
  ]},
  { name: 'Events & Shows', icon: <Ticket className="w-8 h-8" />, desc: 'Cultural shows, concerts, and special events worldwide', items: [
    { title: 'La Perle Dubai', location: 'Dubai', price: 'Rs.6,000', image: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400' },
    { title: 'Thai Boxing Show', location: 'Bangkok', price: 'Rs.2,500', image: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=400' },
    { title: 'Bali Kecak Dance', location: 'Bali', price: 'Rs.1,000', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400' },
  ]},
];

export default function TicketsPage() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', destination: '', travel_date: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/service-inquiry`, { ...form, service_type: 'tickets' });
      setSuccess(true);
      setForm({ name: '', phone: '', destination: '', travel_date: '', message: '' });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div data-testid="tickets-page" className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <div className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Experiences</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">Book Tickets & Experiences</h1>
          <p className="font-body text-base text-gray-400 max-w-2xl mx-auto">Theme parks, city tours, adventure activities, and events — all in one place</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {ACTIVITIES.map((cat, i) => (
              <button key={cat.name} onClick={() => setActiveCategory(i)} data-testid={`ticket-tab-${cat.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-body text-sm font-medium transition-all ${activeCategory === i ? 'bg-gold text-white shadow-lg shadow-gold/25' : 'bg-navy-50 text-navy hover:bg-gray-200'}`}>
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>

          {/* Activity Cards */}
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-navy mb-2">{ACTIVITIES[activeCategory].name}</h2>
            <p className="font-body text-sm text-gray-500 mb-8">{ACTIVITIES[activeCategory].desc}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ACTIVITIES[activeCategory].items.map(item => (
                <div key={item.title} data-testid={`ticket-item-${item.title.toLowerCase().replace(/\s/g, '-')}`}
                  className="pkg-card bg-white rounded-3xl overflow-hidden border border-gray-100">
                  <div className="relative h-48">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-xs font-body px-3 py-1 rounded-full flex items-center gap-1 text-navy">
                      <Star className="w-3 h-3 fill-gold text-gold" /> 4.7
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-navy">{item.title}</h3>
                    <p className="font-body text-xs text-gray-500 mt-1 flex items-center gap-1"><Compass className="w-3 h-3" /> {item.location}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <div>
                        <span className="font-body text-xs text-gray-400">Starting from</span>
                        <p className="font-heading text-xl font-bold text-gold">{item.price}</p>
                      </div>
                      <a href={`https://wa.me/919179992213?text=${encodeURIComponent(`Hi! I want to book ${item.title} (${item.location}) at ${item.price}. Please share details!`)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="bg-gold text-white rounded-full px-5 py-2 text-xs font-body font-medium hover:bg-gold-dark transition-all flex items-center gap-1">
                        Book Now <ArrowRight className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form */}
      <div className="section-padding bg-navy">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Inquire Now</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Explore Activities</h2>
          </div>
          {success ? (
            <div data-testid="ticket-form-success" className="text-center p-10 rounded-3xl bg-white/10 border border-white/20">
              <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Inquiry Submitted!</h3>
              <p className="font-body text-sm text-gray-400">We'll share the best options with you shortly.</p>
              <button onClick={() => setSuccess(false)} className="mt-4 font-body text-sm text-gold hover:underline">Submit another</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="ticket-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Full Name" className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  placeholder="Phone Number" className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" value={form.destination} onChange={e => setForm({...form, destination: e.target.value})}
                  placeholder="Activity / Destination" className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="date" value={form.travel_date} onChange={e => setForm({...form, travel_date: e.target.value})}
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <textarea rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                placeholder="Tell us what you'd like to experience..." className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold resize-none" />
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Explore Activities</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
